/**
 * SettingsModal - Settings modal with language selection
 *
 * @see docs/backlog.md Request 5
 * @see FRONTEND.md for styling guidelines
 */

import { useI18n, type Language } from '../i18n';

export interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** ルール説明を表示するコールバック（オプション） */
  onShowRules?: () => void;
  /** ゲームをリセットするコールバック（オプション） */
  onResetGame?: () => void;
  /** ターン開始に戻すコールバック（オプション） */
  onUndoToTurnStart?: () => void;
  /** ターン開始に戻すが可能かどうか */
  canUndoToTurnStart?: boolean;
}

/**
 * Settings Modal Component
 */
export function SettingsModal({
  isOpen,
  onClose,
  onShowRules,
  onResetGame,
  onUndoToTurnStart,
  canUndoToTurnStart,
}: SettingsModalProps) {
  const { language, setLanguage, t } = useI18n();

  if (!isOpen) return null;

  const handleLanguageChange = (newLang: Language) => {
    setLanguage(newLang);
  };

  const handleShowRules = () => {
    onClose();
    onShowRules?.();
  };

  const handleResetGame = () => {
    if (window.confirm(t.settings.resetGameConfirm)) {
      onResetGame?.();
    }
  };

  const handleUndoToTurnStart = () => {
    if (window.confirm(t.settings.undoToTurnStartConfirm)) {
      onClose();
      onUndoToTurnStart?.();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-foreground/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-card rounded-lg shadow-xl max-w-md w-full mx-4 p-3 md:p-6 max-h-[90vh] overflow-y-auto animate-scale-in">
        {/* Header */}
        <div className="flex justify-between items-center mb-3 md:mb-6 pb-2 md:pb-4 border-b border-border">
          <h2
            id="settings-title"
            className="font-heading text-base md:text-xl font-semibold text-foreground"
          >
            {t.settings.title}
          </h2>
          <button
            className="btn-ghost px-2 py-1 md:px-3"
            onClick={onClose}
            aria-label={t.settings.close}
          >
            ✕
          </button>
        </div>

        {/* Language Selection */}
        <div className="space-y-3 md:space-y-4">
          <div>
            <h3 className="text-xs md:text-sm font-medium text-muted-foreground mb-2 md:mb-3">
              {t.settings.language}
            </h3>
            <div className="grid grid-cols-2 gap-2 md:gap-3">
              <button
                className={`
                  p-2 md:p-4 rounded-lg border-2 transition-all text-left
                  ${language === 'en'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50'}
                `}
                onClick={() => handleLanguageChange('en')}
              >
                <div className="flex items-center gap-1.5 md:gap-2">
                  <span className="text-lg md:text-2xl">🇬🇧</span>
                  <div>
                    <div className="font-medium text-foreground text-sm md:text-base">
                      {t.settings.languageEnglish}
                    </div>
                    <div className="text-[10px] md:text-xs text-muted-foreground">
                      English
                    </div>
                  </div>
                </div>
                {language === 'en' && (
                  <div className="mt-1 md:mt-2 text-[10px] md:text-xs text-primary font-medium">
                    ✓ Selected
                  </div>
                )}
              </button>

              <button
                className={`
                  p-2 md:p-4 rounded-lg border-2 transition-all text-left
                  ${language === 'ja'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50'}
                `}
                onClick={() => handleLanguageChange('ja')}
              >
                <div className="flex items-center gap-1.5 md:gap-2">
                  <span className="text-lg md:text-2xl">🇯🇵</span>
                  <div>
                    <div className="font-medium text-foreground text-sm md:text-base">
                      {t.settings.languageJapanese}
                    </div>
                    <div className="text-[10px] md:text-xs text-muted-foreground">
                      Japanese
                    </div>
                  </div>
                </div>
                {language === 'ja' && (
                  <div className="mt-1 md:mt-2 text-[10px] md:text-xs text-primary font-medium">
                    ✓ 選択中
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* ゲーム操作 */}
          {(onShowRules || onResetGame || onUndoToTurnStart) && (
            <div className="pt-3 md:pt-4 border-t border-border space-y-2 md:space-y-3">
              {onShowRules && (
                <button
                  className="btn-secondary w-full py-1.5 md:py-2 text-sm md:text-base"
                  onClick={handleShowRules}
                >
                  {t.settings.showRules}
                </button>
              )}
              {onUndoToTurnStart && (
                <button
                  className="btn-secondary w-full py-1.5 md:py-2 text-sm md:text-base"
                  onClick={handleUndoToTurnStart}
                  disabled={!canUndoToTurnStart}
                >
                  {t.settings.undoToTurnStart}
                </button>
              )}
              {onResetGame && (
                <button
                  className="btn-ghost w-full py-1.5 md:py-2 text-sm md:text-base text-destructive hover:bg-destructive/10"
                  onClick={handleResetGame}
                >
                  {t.settings.resetGame}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-3 md:mt-6 pt-3 md:pt-4 border-t border-border">
          <button
            className="btn-primary w-full py-1.5 md:py-2 text-sm md:text-base"
            onClick={onClose}
          >
            {t.settings.close}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SettingsModal;
